import type {
	DeviceNotification,
	DeviceNotificationPermission,
	DeviceNotificationPort,
} from '@Domain/Ports/Notification/DeviceNotificationPort';
import mapBrowserPermission from '@Infra/Notification/mapBrowserPermission';

type NotificationConstructor = typeof Notification;

/**
 * Local notifications only: no push subscription, no VAPID key, no network call. Both browser surfaces
 * are received by constructor injection, defaulted from the global scope at construction time — never
 * captured at module load — so that jsdom, where both are `undefined`, can construct this adapter and
 * exercise `permission()` independently of `show()`.
 */
class WebDeviceNotificationAdapter implements DeviceNotificationPort {
	constructor(
		private readonly notificationConstructor:
			| NotificationConstructor
			| undefined = globalThis.Notification,
		private readonly serviceWorkerContainer: ServiceWorkerContainer | undefined = globalThis.navigator
			?.serviceWorker,
	) {}

	permission(): DeviceNotificationPermission {
		if (this.notificationConstructor === undefined) {
			return 'unsupported';
		}
		return mapBrowserPermission(this.notificationConstructor.permission);
	}

	async requestPermission(): Promise<DeviceNotificationPermission> {
		if (this.notificationConstructor === undefined) {
			return 'unsupported';
		}
		const permission = await this.notificationConstructor.requestPermission();
		return mapBrowserPermission(permission);
	}

	/**
	 * A silent no-op when neither a service worker registration nor a `Notification` constructor is
	 * available is deliberate: raising here would fail an unrelated screen over a missed notification.
	 * A caller that needs to know the capability upfront queries `permission()`, which reports
	 * `'unsupported'` in that same situation.
	 */
	async show(notification: DeviceNotification): Promise<void> {
		const options: NotificationOptions = {
			body: notification.body,
			tag: notification.tag,
			data: { targetPath: notification.targetPath },
		};

		// A service worker registration is the normal path on the target device: on Android, a
		// page-constructed `Notification` is refused outright. The constructor below is the degraded
		// fallback for a page with no registration, not the preferred route.
		const registration = await this.serviceWorkerContainer?.getRegistration();
		if (registration !== undefined) {
			await registration.showNotification(notification.title, options);
			return;
		}

		if (this.notificationConstructor === undefined) {
			return;
		}

		new this.notificationConstructor(notification.title, options);
	}
}

export default WebDeviceNotificationAdapter;
